import * as Sentry from "@sentry/react";

// Query params that must be stripped from URLs before sending to Sentry.
// Any param that is an auth token, tracking ID, or brand secret.
//
// Exported so the independent client-error beacon (clientErrorBeacon.ts) can
// redact the same names out of free-text error messages. One list, one place to
// add a newly minted secret param — a second copy would drift. Note the
// deliberate NON-sharing documented in auth/keycloak.ts: its OAuth param list
// is a different set for a different job and must stay separate.
export const SENSITIVE_QUERY_PARAMS = new Set([
  "code",           // OAuth callback codes (Shopify, etc.)
  "state",          // OAuth state tokens
  "token",
  "access_token",
  "refresh_token",
  "id_token",
  "ltq_key",        // SDK key
  "ltq_vid",        // Visitor ID (PII under GDPR)
  "ltq_aid",        // Arm ID (tracking)
  "ltq_oid",        // Origin ID (tracking)
  "ltq_pid",        // Page ID (tracking)
  "sdk_key",
  "api_key",
]);

// Sentinel base used only to parse relative URLs (those starting with "/").
// Never leaks into the output — we return a relative form for relative inputs.
const RELATIVE_URL_BASE = "http://scrub.local";

// Exported for clientErrorBeacon.ts — see the note on SENSITIVE_QUERY_PARAMS.
export function scrubUrl(url: string): string {
  // Detect relative ("/foo"), absolute ("https://..."), and garbage.
  const isRelative = url.startsWith("/") && !url.startsWith("//");
  try {
    const parsed = isRelative
      ? new URL(url, RELATIVE_URL_BASE)
      : new URL(url);
    // Scrub query params
    for (const key of Array.from(parsed.searchParams.keys())) {
      if (SENSITIVE_QUERY_PARAMS.has(key.toLowerCase())) {
        parsed.searchParams.set(key, "[Filtered]");
      }
    }
    // ALWAYS strip the fragment. Keycloak uses response_mode=fragment for
    // the OIDC callback, so the OAuth code / state / session_state land in
    // the `#...` portion of the URL and never in the query string. Rather
    // than maintain a fragment-param allowlist, drop the whole hash — it's
    // client-side only and useless for debugging server-visible errors.
    parsed.hash = "";
    // Preserve relative-URL shape when the input was relative so breadcrumb
    // data stays recognisable to whoever reads the Sentry event.
    if (isRelative) {
      return parsed.pathname + parsed.search;
    }
    return parsed.toString();
  } catch {
    return "[invalid-url]";
  }
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // no-op locally

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? "staging",
    release: import.meta.env.VITE_SENTRY_RELEASE,
    sendDefaultPii: false, // explicit: don't capture IP, user agent details, etc.
    // Browser-extension noise, not application errors (LUTIQ-C8). Chrome
    // throws these verbatim strings from chrome.runtime messaging when an
    // extension talks to a content script / service worker that is no longer
    // listening (extension reloaded or updated mid-session). They reach us as
    // unhandled promise rejections with no stack frames, so matching the
    // message is the only filter that can catch them. Nothing first-party can
    // produce them: no code here calls chrome.runtime or sendMessage (the one
    // postMessage is MobilePreviewDrawer's iframe ping, which throws none of
    // these), so this list cannot mask a real bug. Keep it to that one
    // evidence-backed family — not a general third-party denylist.
    ignoreErrors: [
      "Could not establish connection. Receiving end does not exist",
      "A listener indicated an asynchronous response by returning true, but the message channel closed before a response was received",
    ],
    // Trace propagation: only propagate sentry-trace to same-origin /api calls.
    // Keycloak / third-party URLs excluded so tokens can't leak via headers.
    tracePropagationTargets: [/^\/api/],
    integrations: [
      Sentry.browserTracingIntegration(),
      // NOTE: Session Replay is added dynamically via enableSentryReplay()
      // after Keycloak finishes processing the OAuth callback. Replay's
      // internal `urls` array captures window.location.href at init time,
      // which would otherwise leak the OAuth code / state / session_state
      // from the fragment (Keycloak uses response_mode=fragment) before
      // Keycloak-js has a chance to clean the URL via history.replaceState.
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event) {
      // Scrub URLs in the event
      if (event.request?.url) {
        event.request.url = scrubUrl(event.request.url);
      }
      // Scrub separate query_string field (defense in depth — matches Python/Java scrubbers)
      if (event.request?.query_string) {
        event.request.query_string = "[Filtered]";
      }
      // Scrub headers — remove Authorization and Cookie entirely
      if (event.request?.headers) {
        delete event.request.headers["Authorization"];
        delete event.request.headers["authorization"];
        delete event.request.headers["Cookie"];
        delete event.request.headers["cookie"];
      }
      // Strip query strings from breadcrumbs that record navigation
      if (event.breadcrumbs) {
        for (const crumb of event.breadcrumbs) {
          if (crumb.data?.url && typeof crumb.data.url === "string") {
            crumb.data.url = scrubUrl(crumb.data.url);
          }
          if (crumb.data?.to && typeof crumb.data.to === "string") {
            crumb.data.to = scrubUrl(crumb.data.to);
          }
          if (crumb.data?.from && typeof crumb.data.from === "string") {
            crumb.data.from = scrubUrl(crumb.data.from);
          }
        }
      }
      // Never send localStorage contents (Sentry doesn't by default, but defense in depth)
      if (event.extra) {
        delete event.extra.localStorage;
        delete event.extra.sessionStorage;
      }
      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      // Drop console.log breadcrumbs entirely (may contain user data from debug logs)
      if (breadcrumb.category === "console" && breadcrumb.level !== "error") {
        return null;
      }
      return breadcrumb;
    },
  });

  // Set user context with minimal identifying info — only internal user ID, never email
  // (Done separately in AuthContext after Keycloak init)
}

export function setSentryUser(keycloakSub: string | undefined) {
  if (!keycloakSub) {
    Sentry.setUser(null);
    return;
  }
  // Only the Keycloak `sub` (UUID). No email, no name.
  Sentry.setUser({ id: keycloakSub });
}

let replayEnabled = false;

/**
 * Lazily add the Session Replay integration after Keycloak-js has processed
 * the OAuth callback and cleaned the URL. Replay captures `window.location.href`
 * into its internal `urls` array at init time — if we let it start before
 * Keycloak cleans the fragment, the OAuth code / state / session_state leaks
 * into the replay envelope metadata where `beforeSend` can't see it.
 *
 * Idempotent: safe to call multiple times. Only the first call installs Replay.
 */
export function enableSentryReplay() {
  if (replayEnabled) return;
  const client = Sentry.getClient();
  if (!client) return; // initSentry() was never called (e.g. local dev, no DSN)
  replayEnabled = true;
  client.addIntegration(
    Sentry.replayIntegration({
      // Aggressive default-deny: mask everything, unmask explicit .sentry-unmask
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
      // Block specific known-sensitive elements (even if text masking fails)
      block: [
        '[data-sensitive]',
        '.sdk-key',
        '.api-key',
        'input[type="password"]',
        'input[name*="token"]',
        'input[name*="secret"]',
        'input[name*="key"]',
      ],
      // Selectively unmask safe UI chrome (button labels, headings)
      unmask: ['.sentry-unmask'],
      // Network capture: don't capture request/response bodies
      networkDetailAllowUrls: [], // empty = never capture body details
    }),
  );
}
